import { FormControl, FormErrorMessage, FormLabel } from "@chakra-ui/react";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React, { useMemo } from "react";
import {
  Controller,
  FieldValues,
  UseFormReturn,
  FieldPath,
  get,
} from "react-hook-form";
import { useQueryDataSources } from "../../../../store/useQueryDataSources";
import { StandardSelectOption } from "../../../selector/parts/SelectOption";
import { SingleSelector } from "../../../selector/SingleSelector";

type NetworkSelectorFieldProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: FieldPath<T>;
  networks?: DataSourceWithNetworks[];
  isDisabled?: boolean;
};

/**
 * The Network selector used on all Wallet data sources to select which
 * blockchain should be synced.
 */
export function NetworkSelectorField<T extends FieldValues>({
  form,
  name,
  networks,
  isDisabled,
}: NetworkSelectorFieldProps<T>): React.ReactElement {
  const error = get(form.formState.errors, name);

  const dataSourcesQuery = useQueryDataSources();

  const options: StandardSelectOption[] = useMemo(() => {
    if (!dataSourcesQuery.data || !networks) {
      return [];
    }

    const networkIds = new Set(networks.map((network) => network._id));

    return dataSourcesQuery.data
      .filter((dataSource) => networkIds.has(dataSource._id))
      .map((dataSource) => {
        const label = dataSource.name;
        const aliases = dataSource.aliases ?? [];

        return {
          value: dataSource._id!,
          keywords: [label, ...aliases],
          data: {
            label,
            icon: dataSource.image,
          },
        };
      });
  }, [dataSourcesQuery.data, networks]);

  return (
    <FormControl
      isRequired
      isDisabled={isDisabled ?? form.formState.isSubmitting}
      isInvalid={!!error}
    >
      <FormLabel>Network</FormLabel>
      <Controller
        control={form.control}
        rules={{
          required: "Network is required",
        }}
        name={name}
        render={({ field }) => {
          return (
            <SingleSelector
              moveSelectedToTop
              options={options}
              isDisabled={isDisabled ?? form.formState.isSubmitting}
              isLoading={dataSourcesQuery.isLoading}
              isInvalid={!!error}
              isRequired
              enableSearch
              value={field.value || undefined}
              placeholder="Select a network"
              onChange={(value) => {
                field.onChange(value ?? "");
              }}
              buttonProps={{
                width: "full",
              }}
              popoverProps={{
                matchWidth: true,
              }}
              autoSelectSingleItem
            />
          );
        }}
      />

      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
}
